import React from 'react';
import { Box, Button, Grid2 as Grid, Paper, Typography } from '@mui/material';
import { lighten, styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { scroller } from 'react-scroll';
import { Link as ButtonLink } from 'react-router-dom';

import VoteButton from '../VoteButton';
import ReplyButton from '../ReplyButton';
import UpdateMyIdeaButton from '../UpdateMyIdeaButton';
import { interactionsBoxTitle } from '../../PostSidePrimaryActions/utils';
import ScrollToAnswerButton from '../../PostSidePrimaryActions/ScrollToAnswerButton';
import { allStepsComing } from '../../../../Utils/Steps';
import scrollToOptions from '../../../../Utils/scrollToOptions';
import Analytics from '../../../../Services/AnalyticsService';
import SecuredTitleIcon from '../../SecuredTitleIcon';
import ContributionButton from './ContributionButton';
import { VoteTypes, PostCategories } from '../../../../Utils/Types';

function scrollTo(scrollToID: any, post: any) {
  Analytics.track('Post', 'Scroll to', scrollToID, {
    'Post Type': post.type,
    'Post ID': post.id,
  });
  scroller.scrollTo(scrollToID, scrollToOptions);
}

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop: any) => prop !== 'hasBackground',
})(({ theme, variant, hasBackground }: any) => {
  if (!hasBackground) {
    return {};
  }

  return {
    backgroundColor: variant === 'outlined' ? '' : lighten(theme.palette.primary.main, 0.93),
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 2,
    border: variant === 'outlined' ? `solid 1px ${theme.palette.grey[200]}` : '',
  };
});

function InteractionsBox({
  post,
  space,
  ctaVariant,
  direction,
  isResponsive,
  hasBackground,
  onClickVote,
  onlyPrimaryActions = false,
  showLabel,
  trackingProps,
  size = 'large',
}: any) {
  const actionsToShow = onlyPrimaryActions
    ? post.actions?.filter((action: any) => action.is_primary === true)
    : post.actions?.filter((action: any) => action.is_primary_only !== true);
  const postComing = allStepsComing(post);

  const buttonSize = isResponsive ? 12 / actionsToShow.length : 'auto';

  const actionBtns: Record<string, any> = {
    see_the_answer: () => <ScrollToAnswerButton />,
    comment: () => (
      <ReplyButton post={post} role={space.current_user_role} ctaVariant={ctaVariant} />
    ),
    vote_support: () => (
      <VoteButton
        reaction={VoteTypes.SUPPORT}
        post={post}
        isResponsive={isResponsive}
        onClickVote={onClickVote}
        showLabel={showLabel}
        trackingProps={trackingProps}
        size={size}
      />
    ),
    vote_not_support: () => (
      <VoteButton
        reaction={VoteTypes.NOT_SUPPORT}
        post={post}
        isResponsive={isResponsive}
        onClickVote={onClickVote}
        trackingProps={trackingProps}
        showLabel={showLabel}
        size={size}
      />
    ),
    update: () => <UpdateMyIdeaButton post={post} space={space} />,
    submit_a_proposal: () => (
      <ContributionButton post={post} space={space} postComing={postComing} />
    ),
    see_proposals: () => (
      <>
        <SecuredTitleIcon post={post} />

        <Button
          color="primary"
          size="large"
          variant="outlined"
          data-testid="add-or-see-idea-button"
          disabled={postComing}
          onClick={() => scrollTo('children-ideas-list', post)}
          fullWidth={isResponsive}
        >
          <FormattedMessage id="BUTTONS.VIEW_IDEAS" />
        </Button>
      </>
    ),
    study_participate: () => (
      <>
        <SecuredTitleIcon post={post} />

        <Button
          component={ButtonLink}
          to="#"
          color="primary"
          size="large"
          variant="outlined"
          disabled={postComing}
          onClick={() => scrollTo('study-content-tab', post)}
          fullWidth={isResponsive}
        >
          <FormattedMessage id="POSTS.DETAILS.STUDY.PARTICIPATE" />
        </Button>
      </>
    ),
    see_study_results: () => (
      <Button
        component={ButtonLink}
        to="#"
        color="primary"
        size="large"
        variant="outlined"
        onClick={() => scrollTo('study-content-tab', post)}
      >
        <FormattedMessage id="POSTS.DETAILS.STUDY.SEE_RESULTS" />
      </Button>
    ),
  };

  return (
    <>
      {/* @ts-expect-error TS(2769): No overload matches this call. */}
      <StyledPaper elevation={hasBackground ? 1 : 0} hasBackground={hasBackground}>
        {hasBackground && (
          <Box sx={{ mb: 2 }}>
            <Typography
              color="textPrimary"
              variant="h6"
              component={PostCategories.MESSAGING.includes(post.type) ? 'h2' : 'h3'}
              data-chromatic="ignore"
              align="center"
            >
              <FormattedMessage
                id={interactionsBoxTitle(post)}
                values={{ space: space.name, author: post.included.author.full_name }}
              />
            </Typography>
          </Box>
        )}
        <Grid
          container
          direction={direction}
          spacing={1.5}
          sx={{ justifyContent: direction === 'row' ? 'center' : 'start' }}
        >
          {actionsToShow?.map(({ name }: any) => (
            <Grid key={name} size={buttonSize}>
              {actionBtns[name]()}
            </Grid>
          ))}
        </Grid>
      </StyledPaper>
    </>
  );
}

InteractionsBox.propTypes = {
  ctaVariant: PropTypes.string,
  direction: PropTypes.string,
  post: PropTypes.shape({
    status: PropTypes.string,
    type: PropTypes.string,
    canComment: PropTypes.bool,
  }),
  space: PropTypes.shape({
    current_user_role: PropTypes.string,
  }),
  onlyPrimaryActions: PropTypes.bool,
  showLabel: PropTypes.bool,
  isResponsive: PropTypes.bool,
  hasBackground: PropTypes.bool,
  size: PropTypes.string,
};

export default InteractionsBox;
